import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import {
  FaArrowLeft,
  FaStar,
  FaEye,
  FaShare,
  FaEnvelope,
  FaPhone,
  FaArrowRight,
} from "react-icons/fa";
import { fetchProductById } from "../store/slices/productSlice";
import { formatCurrency, formatDate } from "../utils/helpers";
import { COMPANY_INFO } from "../utils/constants";

const ProductDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { currentProduct, loading, error } = useSelector(
    (state) => state.products
  );
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    if (id) {
      dispatch(fetchProductById(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (currentProduct) {
      setSelectedImage(0);
    }
  }, [currentProduct]);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: currentProduct.name,
          text: currentProduct.description,
          url: window.location.href,
        });
      } catch (err) {
        console.log("Error sharing:", err);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert("Link copied to clipboard!");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="h-12 w-12 border-4 border-blue-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !currentProduct) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {error || "Product not found"}
          </h2>
          <Link
            to="/products"
            className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white font-semibold px-6 py-3 rounded-full transition-all duration-200"
          >
            <FaArrowLeft className="text-sm" /> Back to Products
          </Link>
        </div>
      </div>
    );
  }

  const specifications = currentProduct.specifications || {};
  const additionalImages = currentProduct.additionalImages || [];

  // Main image: single string field
  const mainImageSrc = currentProduct.image || "/placeholder-image.jpg";
  // Thumbnail pool: additionalImages array (if any)
  const thumbnails =
    additionalImages.length > 0
      ? [mainImageSrc, ...additionalImages]
      : [];

  const displayedImage =
    thumbnails.length > 0 ? thumbnails[selectedImage] : mainImageSrc;

  return (
    <>
      <Helmet>
        <title>{currentProduct.name} - OM Maruti Creation</title>
        <meta name="description" content={currentProduct.description} />
      </Helmet>

      {/* ── Breadcrumb ── */}
      <section className="py-3 bg-gray-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <nav className="flex items-center gap-2 text-sm text-gray-500">
            <Link to="/" className="hover:text-blue-700 transition">
              Home
            </Link>
            <span className="text-gray-300">/</span>
            <Link to="/products" className="hover:text-blue-700 transition">
              Products
            </Link>
            <span className="text-gray-300">/</span>
            <span className="text-gray-900 font-medium">
              {currentProduct.name}
            </span>
          </nav>
        </div>
      </section>

      {/* ── Main Product Section ── */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-14">
            {/* Left: Images */}
            <div>
              <div className="rounded-2xl overflow-hidden mb-4 border border-gray-100 shadow-sm">
                <img
                  src={displayedImage}
                  alt={currentProduct.name}
                  className="w-full h-[480px] object-cover"
                />
              </div>

              {/* Thumbnails */}
              {thumbnails.length > 1 && (
                <div className="flex gap-3 overflow-x-auto pb-1">
                  {thumbnails.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition ${
                        selectedImage === index
                          ? "border-blue-700"
                          : "border-gray-200 hover:border-blue-300"
                      }`}
                    >
                      <img
                        src={img}
                        alt={`${currentProduct.name} view ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right: Product Info */}
            <div>
              {/* Back + Share */}
              <div className="flex items-center gap-3 mb-6">
                <Link
                  to="/products"
                  className="inline-flex items-center gap-2 text-gray-500 hover:text-blue-700 text-sm font-medium transition"
                >
                  <FaArrowLeft className="text-xs" /> Back to Products
                </Link>
                <button
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 text-gray-500 hover:text-blue-700 text-sm font-medium transition ml-auto"
                >
                  <FaShare className="text-xs" /> Share
                </button>
              </div>

              {/* Category badge */}
              {currentProduct.category && (
                <span className="inline-block bg-blue-50 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full mb-4">
                  {currentProduct.category}
                </span>
              )}

              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 leading-tight">
                {currentProduct.name}
              </h1>

              {/* View count */}
              <div className="flex items-center gap-2 mt-3 text-sm text-gray-400">
                <FaEye />
                <span>{currentProduct.viewCount || 0} views</span>
              </div>

              {/* Price */}
              {currentProduct.price && (
                <div className="mt-5">
                  <span className="text-3xl font-bold text-blue-700">
                    {formatCurrency(currentProduct.price)}
                  </span>
                </div>
              )}

              {/* Description */}
              <div className="mt-6">
                <h3 className="text-base font-bold text-gray-900 mb-2">
                  Description
                </h3>
                <p className="text-gray-500 leading-relaxed">
                  {currentProduct.description}
                </p>
              </div>

              {/* Specifications */}
              {Object.keys(specifications).length > 0 && (
                <div className="mt-8">
                  <h3 className="text-base font-bold text-gray-900 mb-4">
                    Specifications
                  </h3>
                  <div className="rounded-xl border border-gray-100 overflow-hidden">
                    {Object.entries(specifications).map(([key, value], i) => (
                      <div
                        key={key}
                        className={`flex justify-between px-5 py-3 text-sm ${
                          i % 2 === 0 ? "bg-gray-50" : "bg-white"
                        }`}
                      >
                        <span className="font-medium text-gray-700 capitalize">
                          {key.replace(/([A-Z])/g, " $1").trim()}
                        </span>
                        <span className="text-gray-500">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Rating */}
              <div className="flex items-center gap-2 mt-8">
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <FaStar
                      key={star}
                      className={`text-base ${
                        star <= 4 ? "text-yellow-400" : "text-gray-200"
                      }`}
                    />
                  ))}
                </div>
                <span className="text-sm text-gray-400">
                  4.0 — Based on customer reviews
                </span>
              </div>

              {/* CTA card */}
              <div className="mt-8 bg-blue-50 rounded-2xl p-6 border border-blue-100">
                <h3 className="text-base font-bold text-gray-900 mb-2">
                  Interested in this product?
                </h3>
                <p className="text-gray-500 text-sm mb-5">
                  Contact us for pricing, samples, and custom requirements.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <a
                    href={`tel:${COMPANY_INFO.phone}`}
                    className="inline-flex items-center justify-center gap-2 bg-blue-700 hover:bg-blue-800 text-white font-semibold px-5 py-3 rounded-full transition-all duration-200 text-sm"
                  >
                    <FaPhone /> Call Now
                  </a>
                  <Link
                    to={`/contact?product=${currentProduct._id}`}
                    className="inline-flex items-center justify-center gap-2 border-2 border-blue-700 text-blue-700 hover:bg-blue-700 hover:text-white font-semibold px-5 py-3 rounded-full transition-all duration-200 text-sm"
                  >
                    <FaEnvelope /> Get Quote
                  </Link>
                </div>
              </div>

              {/* Additional info */}
              <div className="mt-6 text-xs text-gray-400 space-y-1">
                <p>
                  Last updated:{" "}
                  {formatDate(
                    currentProduct.updatedAt || currentProduct.createdAt
                  )}
                </p>
                {!currentProduct.isActive && (
                  <p className="text-red-500">
                    This product is currently unavailable.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA Bottom Section ── */}
      <section className="py-16 bg-blue-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white">
            Explore More Products
          </h2>
          <p className="mt-4 text-blue-200 text-lg max-w-xl mx-auto">
            Browse our full collection of premium men's garments manufactured
            with quality and care.
          </p>
          <div className="mt-8">
            <Link
              to="/products"
              className="inline-flex items-center gap-2 bg-white text-blue-700 hover:bg-blue-50 font-semibold px-8 py-4 rounded-full transition-all duration-200 shadow-sm hover:shadow-md"
            >
              View All Products <FaArrowRight className="text-sm" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default ProductDetail;
